import { Box, LinearProgress, Typography } from "@mui/material";
import { useCelleraTheme } from "theme/useCelleraTheme";

type FileProgressProps = {
  fileName: string;
  progress: number; // 0-100
  bytesTransferred: number;
  totalBytes: number;
};

const FileProgress: React.FC<FileProgressProps> = ({
  fileName,
  progress,
  bytesTransferred,
  totalBytes,
}: FileProgressProps) => {
  const { colors } = useCelleraTheme();

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${colors.border.light}`,
        backgroundColor: colors.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: colors.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            mr: 1,
          }}
        >
          {fileName}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: "12px",
            color: colors.text.secondary,
            fontWeight: 500,
          }}
        >
          {Math.round(progress)}%
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: "3px",
          backgroundColor: colors.background.grayLight,
          "& .MuiLinearProgress-bar": {
            borderRadius: "3px",
            backgroundColor: colors.primary.main,
          },
        }}
      />

      <Typography
        variant="caption"
        sx={{
          fontSize: "12px",
          color: colors.text.secondary,
        }}
      >
        {formatFileSize(bytesTransferred)} / {formatFileSize(totalBytes)}
      </Typography>
    </Box>
  );
};

export default FileProgress;
